import React, { useState } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { notify } from '../../../notifications';
import { MonthDay } from './editApi';
import {
  DayValues, EditField, FieldGroup, formatNumber, isIntField, isWeekend, parseNumberInput, weekdayShort
} from './editCalc';

const BORDER = '1px solid #e2e8f0';
const DATE_COL_WIDTH = 116;
const COL_WIDTH = 128;
const GROUP_ROW_HEIGHT = 34;
const RAW_ALLOWED = /[^\d\-−.,\s]/g;

/** Колонка сетки: поле отчета, порядковый номер среди редактируемых полей (-1 - только чтение) и выделение значения */
export interface GridColumn {
  field: EditField;
  inputIndex: number;
  strong: boolean;
}

/**
 * Стили всей сетки заданы один раз на контейнере: ячейки - обычные table/td/input с короткими классами,
 * без отдельных sx-объектов на каждую ячейку (таблица месяца - это сотни ячеек).
 */
const gridSx: SxProps<Theme> = {
  overflow: 'auto',
  maxHeight: 'calc(100vh - 330px)',
  minHeight: 320,
  '& table': { borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' },

  '& th': {
    position: 'sticky',
    boxSizing: 'border-box',
    padding: '6px 8px',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontFamily: 'inherit',
    verticalAlign: 'bottom'
  },
  '& th.corner': {
    top: 0, left: 0, zIndex: 4, textAlign: 'left', fontSize: '0.78rem', fontWeight: 700,
    borderBottom: BORDER, borderRight: BORDER
  },
  '& th.grp': {
    top: 0, zIndex: 3, height: GROUP_ROW_HEIGHT, textAlign: 'left', fontSize: '0.74rem', fontWeight: 700,
    lineHeight: 1.3, textTransform: 'uppercase', letterSpacing: '0.4px',
    borderBottom: '1px solid #f1f5f9', borderLeft: BORDER
  },
  '& th.col': {
    top: GROUP_ROW_HEIGHT, zIndex: 3, textAlign: 'right', fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.25,
    color: '#64748b', borderBottom: BORDER, overflowWrap: 'break-word'
  },
  '& th.col.inp': { color: '#334155' },
  '& th.col.imp': { fontWeight: 700 },

  '& tbody tr': { backgroundColor: '#ffffff' },
  '& tbody tr.we': { backgroundColor: '#f8fafc' },
  '& tbody tr:hover': { backgroundColor: '#f1f5f9' },

  '& td': {
    boxSizing: 'border-box',
    padding: '4px 6px',
    borderBottom: '1px solid #f1f5f9',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap'
  },
  '& td.day': {
    position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'inherit', borderRight: BORDER, whiteSpace: 'normal'
  },
  '& td.day .d': { fontSize: '0.84rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 },
  '& td.day .d span': { marginLeft: '6px', fontWeight: 500, color: '#64748b' },
  '& td.day .cap': { fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.2 },
  '& tr.fut td:not(.day)': { opacity: 0.55 },
  '& tr.fut td.day > div': { opacity: 0.55 },

  // Расчетные и внешние значения: просто текст, без поля ввода
  '& td.ro': {
    padding: '4px 15px 4px 6px', textAlign: 'right', fontSize: '0.84rem', fontWeight: 500, lineHeight: '32px',
    color: '#475569', fontVariantNumeric: 'tabular-nums'
  },
  '& td.ro.s': { fontWeight: 700 },
  '& td.ro.drv': { color: '#2563eb' },

  '& input': {
    display: 'block',
    width: '100%',
    height: 32,
    boxSizing: 'border-box',
    padding: '0 8px',
    textAlign: 'right',
    font: 'inherit',
    fontSize: '0.84rem',
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    color: '#1e293b',
    backgroundColor: '#ffffff',
    border: '1px solid transparent',
    borderRadius: '8px',
    outline: 'none'
  },
  '& input.s': { fontWeight: 700 },
  '& input:hover:not(:disabled)': { borderColor: '#cbd5e1' },
  '& input.chg': { backgroundColor: '#fffbeb', borderColor: '#fcd34d' },
  '& input.chg:hover:not(:disabled)': { borderColor: '#fbbf24' },
  '& input:focus': { backgroundColor: '#ffffff', borderColor: '#2563eb', boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)' },
  '& input:disabled': { color: '#94a3b8', WebkitTextFillColor: '#94a3b8', backgroundColor: 'transparent' }
};

interface InputCellProps {
  field: EditField;
  value: number;
  row: number;
  col: number;
  changed: boolean;
  strong: boolean;
  disabled: boolean;
  onCommit: (index: number, name: string, value: number) => void;
}

/**
 * Редактируемая ячейка: в покое показывает значение с разделителями разрядов, при вводе - обычный текст.
 * Пустое поле означает 0, некорректный ввод откатывается к прежнему значению с подсказкой.
 * Перерисовывается только при изменении своих параметров, набор текста не затрагивает остальную таблицу.
 */
const InputCell = React.memo(({ field, value, row, col, changed, strong, disabled, onCommit }: InputCellProps) => {
  const [text, setText] = useState<string | null>(null);

  const finish = () => {
    if (text === null) return;
    const parsed = parseNumberInput(text, field);
    setText(null);
    if (parsed.ok) {
      if (parsed.value !== value) onCommit(row, field.name, parsed.value);
    } else {
      notify.warning(`${field.label}: ${parsed.message}`);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const el = e.target;
    setText(String(value));
    window.requestAnimationFrame(() => el.select());
  };

  return (
    <td>
      <input
        className={`${changed ? 'chg' : ''}${strong ? ' s' : ''}`}
        value={text !== null ? text : formatNumber(value, field)}
        onChange={(e) => setText(e.target.value.replace(RAW_ALLOWED, ''))}
        onFocus={handleFocus}
        onBlur={finish}
        disabled={disabled}
        inputMode={isIntField(field) ? 'numeric' : 'decimal'}
        aria-label={field.label}
        data-r={row}
        data-c={col}
      />
    </td>
  );
});

interface DayRowProps {
  index: number;
  day: MonthDay;
  values: DayValues;
  original: DayValues;
  columns: GridColumn[];
  disabled: boolean;
  onCommit: (index: number, name: string, value: number) => void;
}

const DayRow = React.memo(({ index, day, values, original, columns, disabled, onCommit }: DayRowProps) => {
  const caption = day.future ? 'впереди' : !day.exists ? 'нет записи' : '';
  const rowClass = `${isWeekend(day.date) ? 'we' : ''}${day.future ? ' fut' : ''}`;

  return (
    <tr className={rowClass}>
      <td className="day">
        <div className="d">
          {`${day.date.slice(8)}.${day.date.slice(5, 7)}`}
          <span>{weekdayShort(day.date)}</span>
        </div>
        {caption && <div className="cap">{caption}</div>}
      </td>
      {columns.map(({ field, inputIndex, strong }) => {
        const value = values[field.name];
        const edited = value !== original[field.name];
        if (inputIndex < 0 || day.future) {
          return (
            <td key={field.name} className={`ro${strong ? ' s' : ''}${edited && inputIndex < 0 ? ' drv' : ''}`}>
              {formatNumber(value, field)}
            </td>
          );
        }
        return (
          <InputCell
            key={field.name}
            field={field}
            value={value}
            row={index}
            col={inputIndex}
            changed={edited}
            strong={strong}
            disabled={disabled}
            onCommit={onCommit}
          />
        );
      })}
    </tr>
  );
});

const MonthHead = React.memo(({ groups, columns }: { groups: FieldGroup[]; columns: GridColumn[] }) => (
  <thead>
    <tr>
      <th rowSpan={2} className="corner">День</th>
      {groups.map((g) => (
        <th key={g.name} colSpan={g.fields.length} className="grp">{g.name}</th>
      ))}
    </tr>
    <tr>
      {columns.map(({ field, inputIndex }) => (
        <th
          key={field.name}
          className={`col${inputIndex >= 0 ? ' inp' : ''}${field.important ? ' imp' : ''}`}
        >
          {field.label}
        </th>
      ))}
    </tr>
  </thead>
));

/** Навигация по таблице: Enter / стрелка вниз - к тому же полю следующего дня, Shift+Enter / стрелка вверх - предыдущего */
function handleGridKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
  const enter = e.key === 'Enter';
  const down = (enter && !e.shiftKey) || e.key === 'ArrowDown';
  const up = (enter && e.shiftKey) || e.key === 'ArrowUp';
  if (!down && !up) return;

  const target = e.target as HTMLElement;
  if (target.tagName !== 'INPUT') return;
  const cell = target.closest('td');
  if (!cell || !cell.parentElement) return;

  const col = cell.cellIndex;
  let row = down ? cell.parentElement.nextElementSibling : cell.parentElement.previousElementSibling;
  while (row) {
    const sibling = (row as HTMLTableRowElement).cells[col];
    const next = sibling ? sibling.querySelector('input') : null;
    if (next) {
      e.preventDefault();
      next.focus();
      return;
    }
    row = down ? row.nextElementSibling : row.previousElementSibling;
  }
  // Последняя строка: Enter завершает ввод (значение фиксируется), стрелки работают как обычно
  if (enter) (target as HTMLInputElement).blur();
}

interface MonthGridProps {
  days: MonthDay[];
  rows: DayValues[];
  groups: FieldGroup[];
  columns: GridColumn[];
  disabled: boolean;
  gridRef: React.RefObject<HTMLDivElement>;
  onCommit: (index: number, name: string, value: number) => void;
}

/** Таблица «дни x поля» месяца: перерисовываются только строки и ячейки, значения которых изменились */
export const MonthGrid = React.memo(({ days, rows, groups, columns, disabled, gridRef, onCommit }: MonthGridProps) => {
  const tableWidth = DATE_COL_WIDTH + columns.length * COL_WIDTH;

  return (
    <Box ref={gridRef} onKeyDown={handleGridKeyDown} sx={gridSx}>
      <table style={{ width: tableWidth, minWidth: '100%' }}>
        <colgroup>
          <col style={{ width: DATE_COL_WIDTH }} />
          {columns.map(({ field }) => <col key={field.name} style={{ width: COL_WIDTH }} />)}
        </colgroup>
        <MonthHead groups={groups} columns={columns} />
        <tbody>
          {days.map((day, i) => (
            <DayRow
              key={day.date}
              index={i}
              day={day}
              values={rows[i]}
              original={day.values}
              columns={columns}
              disabled={disabled}
              onCommit={onCommit}
            />
          ))}
        </tbody>
      </table>
    </Box>
  );
});

export default MonthGrid;
